import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CaretDownIcon, MagnifyingGlassIcon } from '@phosphor-icons/react';
import {
  MPFCombobox,
  MPFInput,
  MPFLink,
  type MPFComboboxOption,
} from 'volto-ministerio-publico-federal/components/UI';
import {
  normalizar,
  trechoDestacado,
} from 'volto-ministerio-publico-federal/helpers/Servicos';

import type { ContatoIndexado } from './helpers';
import {
  estadoDoContato,
  estadosDosContatos,
  matchContato,
  toTelHref,
  ufsPorEstado,
} from './helpers';
import { IMPRENSA_ROTULOS } from './schema';
import { useAlturaDeLinhas } from './useAlturaDeLinhas';

export interface ContatosPorEstadoProps {
  titulo?: string;
  label?: string;
  placeholder?: string;
  /** Texto do link das linhas que têm `link`. */
  linkRotulo?: string;
  /**
   * `busca` (padrão): campo de texto que filtra na digitação — o desenho da
   * comunicação. `estado`: campo com a lista de estados que filtra enquanto a
   * pessoa digita, para as "Salas de atendimento ao cidadão" da página de
   * Serviços.
   */
  filtro?: 'busca' | 'estado';
  /** Já pareados com o índice original — ver `toContatosIndexados`. */
  contatos: ContatoIndexado[];
  /** Presente só no editor: abre o item na barra lateral. */
  onEditItem?: (index: number) => void;
}

const PLACEHOLDER_PADRAO = 'Busque por estado ou sigla';

const PLACEHOLDER_ESTADO = 'Selecione um estado';

/** Linhas que o painel mostra sem rolar. As demais continuam ali, rolando. */
const LINHAS_VISIVEIS = 3;

/**
 * No celular a linha ocupa duas linhas de texto (e-mail em cima, telefone
 * embaixo), então três delas empurram o resto do cartão para fora da tela —
 * é o que a captura mostrava. Uma linha basta para o painel se anunciar como
 * lista rolável, e o cartão volta a caber.
 */
const LINHAS_VISIVEIS_MOBILE = 1;

/** `md` do Tailwind, o mesmo corte que `AcoesRapidasCard` já usa. */
const CONSULTA_DESKTOP = '(min-width: 768px)';

/**
 * Quantas linhas o painel mostra, conforme a largura da tela.
 *
 * Começa em `LINHAS_VISIVEIS` e só desce depois da hidratação: ler o viewport
 * durante o render faria servidor e cliente discordarem. O `max-height` de CSS
 * já nasce com o valor certo para cada largura (ver `_imprensa.scss`), então
 * não há salto visível — o JavaScript apenas afina a medida para nenhuma linha
 * aparecer pela metade.
 *
 * Com listener, ao contrário do `AcoesRapidasCard`: lá o estado guarda uma
 * escolha do visitante, que um resize apagaria; aqui o valor é derivado só da
 * largura, e girar o aparelho precisa recalcular.
 */
const useLinhasVisiveis = (): number => {
  const [linhas, setLinhas] = useState(LINHAS_VISIVEIS);

  useEffect(() => {
    const consulta = window.matchMedia(CONSULTA_DESKTOP);
    const aplicar = () =>
      setLinhas(consulta.matches ? LINHAS_VISIVEIS : LINHAS_VISIVEIS_MOBILE);

    aplicar();
    consulta.addEventListener('change', aplicar);
    return () => consulta.removeEventListener('change', aplicar);
  }, []);

  return linhas;
};

/**
 * Coluna "Contatos por estado": um campo de busca que filtra, na digitação, a
 * lista logo abaixo.
 *
 * Os resultados ficam num painel fixo, e não num popup — por isso aqui não
 * entra o padrão ARIA combobox, que existe para anunciar uma lista que aparece
 * e some. Bastam o `aria-controls` ligando campo e lista e um `role="status"`
 * com a contagem, que é o que o leitor de tela precisa ouvir a cada tecla.
 *
 * Sem JavaScript o painel já chega renderizado com a lista inteira: o filtro é
 * progressivo, ninguém fica sem os contatos.
 */
export const ContatosPorEstado: React.FC<ContatosPorEstadoProps> = ({
  titulo,
  label,
  placeholder,
  linkRotulo,
  filtro = 'busca',
  contatos,
  onEditItem,
}) => {
  const rotuloDoLink = linkRotulo?.trim() || IMPRENSA_ROTULOS.contatosLink;
  const [termo, setTermo] = useState('');
  const porEstado = filtro === 'estado';
  const estados = useMemo(() => estadosDosContatos(contatos), [contatos]);
  const ufs = useMemo(() => ufsPorEstado(contatos), [contatos]);
  const alvo = normalizar(termo.trim());
  // A pessoa tanto escreve o nome ("Distrito Federal") quanto a UF ("DF"):
  // a UF digitada inteira já escolhe o estado.
  const estadoEscolhido = porEstado
    ? estados.find(
        (nome) =>
          normalizar(nome) === alvo ||
          (ufs.get(nome) ?? []).some((uf) => normalizar(uf) === alvo),
      )
    : undefined;
  const estadosFiltrados = useMemo(
    () =>
      estados.filter(
        (nome) =>
          normalizar(nome).includes(alvo) ||
          (ufs.get(nome) ?? []).some((uf) => normalizar(uf).startsWith(alvo)),
      ),
    [estados, ufs, alvo],
  );
  const listaRef = useRef<HTMLUListElement>(null);
  const baseId = useId();
  const listaId = `${baseId}-lista`;
  const tituloId = `${baseId}-titulo`;

  const resultados = useMemo(
    () =>
      estadoEscolhido
        ? contatos.filter(
            ({ contato }) => estadoDoContato(contato) === estadoEscolhido,
          )
        : contatos.filter(({ contato }) => matchContato(contato, termo)),
    [contatos, termo, estadoEscolhido],
  );

  // O `max-height` do CSS é só a aproximação de quem está sem JavaScript; aqui
  // a altura sai da medida das primeiras linhas, para nenhuma seguinte
  // aparecer pela metade.
  const linhasVisiveis = useLinhasVisiveis();
  const alturaMaxima = useAlturaDeLinhas(listaRef, linhasVisiveis, [
    resultados,
  ]);

  return (
    <section
      className="mpf-imprensa__contatos"
      {...(titulo ? { 'aria-labelledby': tituloId } : {})}
    >
      {titulo && (
        <h3 id={tituloId} className="mpf-imprensa__coluna-titulo">
          {titulo}
        </h3>
      )}

      {porEstado ? (
        <MPFCombobox
          label={label || 'Estado'}
          placeholder={placeholder || PLACEHOLDER_ESTADO}
          value={termo}
          onValueChange={setTermo}
          options={estadosFiltrados.map((nome): MPFComboboxOption => {
            const { antes, trecho, depois } = trechoDestacado(nome, termo);
            const siglas = (ufs.get(nome) ?? []).join(', ');
            return {
              id: nome,
              meta: siglas || undefined,
              content: (
                <>
                  {antes}
                  {trecho && (
                    <mark className="mpf-combobox__marca">{trecho}</mark>
                  )}
                  {depois}
                </>
              ),
            };
          })}
          onSelect={(option) => setTermo(option.id)}
          onSubmit={() => {}}
          submitIcon={<CaretDownIcon aria-hidden="true" />}
          submitLabel="Mostrar os estados"
          statusText={
            estadosFiltrados.length === 1
              ? '1 estado. Use as setas para navegar.'
              : `${estadosFiltrados.length} estados. Use as setas para navegar.`
          }
          emptyContent={
            <p className="mpf-imprensa__estado-vazio">
              {`Nenhum estado encontrado para “${termo.trim()}”.`}
            </p>
          }
          className="mpf-imprensa__estado"
        />
      ) : (
        <MPFInput
          type="search"
          label={label || 'Estado'}
          placeholder={placeholder || PLACEHOLDER_PADRAO}
          value={termo}
          onChange={(event) => setTermo(event.target.value)}
          leadingIcon={<MagnifyingGlassIcon size={18} />}
          aria-controls={listaId}
          className="mpf-imprensa__busca"
        />
      )}

      <p role="status" aria-live="polite" className="sr-only">
        {resultados.length === 1
          ? '1 contato encontrado'
          : `${resultados.length} contatos encontrados`}
      </p>

      {resultados.length === 0 ? (
        <p id={listaId} className="mpf-imprensa__vazio">
          {`Nenhum contato encontrado para “${termo.trim()}”.`}
        </p>
      ) : (
        <ul
          id={listaId}
          ref={listaRef}
          className="mpf-imprensa__lista"
          style={alturaMaxima ? { maxHeight: alturaMaxima } : undefined}
        >
          {resultados.map(({ contato, index }) => {
            const telHref = toTelHref(contato.telefone);
            const cidade = contato.cidade?.trim();
            const link = contato.link?.trim();

            // No editor a linha inteira é o botão que abre o item na barra
            // lateral, e os canais viram texto: `mailto:`/`tel:` dentro de um
            // <button> seria elemento interativo aninhado, e clicar num deles
            // tiraria o editor da página.
            const canais = (
              <span className="mpf-imprensa__canais">
                {cidade && (
                  <span className="mpf-imprensa__canal">{cidade}</span>
                )}

                {contato.email && cidade && (
                  <span className="mpf-imprensa__separador" aria-hidden="true">
                    •
                  </span>
                )}

                {contato.email &&
                  (onEditItem ? (
                    <span className="mpf-imprensa__canal">{contato.email}</span>
                  ) : (
                    <a
                      className="mpf-imprensa__canal"
                      href={`mailto:${contato.email}`}
                    >
                      {contato.email}
                    </a>
                  ))}

                {/* Marcador e telefone no MESMO item do flex: soltos, a
                    quebra de linha deixaria o "•" pendurado no fim da linha
                    de cima. */}
                {contato.telefone && (
                  <span className="mpf-imprensa__canal-par">
                    {(contato.email || cidade) && (
                      <span
                        className="mpf-imprensa__separador"
                        aria-hidden="true"
                      >
                        •
                      </span>
                    )}

                    {telHref && !onEditItem ? (
                      <a className="mpf-imprensa__canal" href={telHref}>
                        {contato.telefone}
                      </a>
                    ) : (
                      <span className="mpf-imprensa__canal">
                        {contato.telefone}
                      </span>
                    )}
                  </span>
                )}

                {/* O rótulo é o mesmo em todas as linhas, então a sigla vai
                    junto em `sr-only`: na lista de links do leitor de tela,
                    dez "Ver endereço e horário" iguais não dizem de quem é
                    cada um (WCAG 2.4.4). */}
                {link && (
                  <span className="mpf-imprensa__canal-par">
                    {(cidade || contato.email || contato.telefone) && (
                      <span
                        className="mpf-imprensa__separador"
                        aria-hidden="true"
                      >
                        •
                      </span>
                    )}

                    {onEditItem ? (
                      <span className="mpf-imprensa__canal mpf-imprensa__canal--link">
                        {rotuloDoLink}
                      </span>
                    ) : (
                      <MPFLink
                        href={link}
                        className="mpf-imprensa__canal mpf-imprensa__canal--link"
                      >
                        {rotuloDoLink}
                        <span className="sr-only">{` — ${contato.unidade}`}</span>
                      </MPFLink>
                    )}
                  </span>
                )}
              </span>
            );

            const conteudo = (
              <>
                <span className="mpf-imprensa__unidade">{contato.unidade}</span>
                {canais}
              </>
            );

            return (
              <li key={contato['@id'] || `${contato.unidade}-${index}`}>
                {onEditItem ? (
                  <button
                    type="button"
                    className="mpf-imprensa__linha mpf-imprensa__linha--editavel"
                    onClick={() => onEditItem(index)}
                    title={`Editar o contato de ${contato.unidade}`}
                  >
                    {conteudo}
                  </button>
                ) : (
                  <div className="mpf-imprensa__linha">{conteudo}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ContatosPorEstado;
